"use client";

import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlagIcon from "@mui/icons-material/Flag";
import RedFlagPopup from "../../components/dialog/redFlagPopup";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import { maskPhone } from "../../helper";
import { FloatChipTable, calcAmount, nowStr, loadRegularChips } from "./floatChipTable.js";

export default function FloatIssueForm({ open, onClose, refresh }) {
  const token = useSelector((state) => state.loginSlice.token);

  const [stockChips, setStockChips]     = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const [phone, setPhone]             = useState("");
  const [searching, setSearching]     = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundUser, setFoundUser]     = useState(null);

  const [issued, setIssued]   = useState({});
  const [form, setForm]       = useState({ date: nowStr(), remarks: "", reference_name: "" });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, date: nowStr() }));
    setStockLoading(true);
    loadRegularChips(token)
      .then((chips) => { setStockChips(chips); setIssued({}); })
      .finally(() => setStockLoading(false));
  }, [open]);

  const handleSearch = async () => {
    const input = phone.trim();
    if (!input) return;
    setSearching(true);
    setSearchError("");
    setFoundUser(null);
    try {
      const param = /^\d{10}$/.test(input) ? `phone=${input}` : `playerId=${input}`;
      const res = await axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token);
      const user = res?.data?.userDetails;
      if (user) setFoundUser(user);
      else setSearchError("No user found.");
    } catch {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const totalAmount   = stockChips.reduce((sum, c) => sum + calcAmount(c.denomination, issued[c.denomination]), 0);
  const floatOpen     = Number(foundUser?.float_outstanding || 0);
  const overStock     = stockChips.some((c) => Number(issued[c.denomination]) > c.quantity);

  const handleSave = async () => {
    setSubmitError("");
    try {
      setLoading(true);
      const chips = stockChips
        .filter((c) => Number(issued[c.denomination]) > 0)
        .map((c) => ({ denomination: c.denomination, quantity: Number(issued[c.denomination]) }));

      const res = await axiosRequest("POST", `${apiBaseUrl}/float-exchange`, token, {
        player_id: foundUser?.playerId || "",
        phone: foundUser?.phone || phone,
        remarks: form.remarks,
        reference_name: form.reference_name.trim(),
        date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        chips,
      });

      if (res?.status === false || res?.data?.status === false) {
        setSubmitError(res?.message || res?.data?.message || "Failed to issue float.");
        return;
      }

      refresh();
      handleClose();
    } catch (err) {
      setSubmitError("Something went wrong.");
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setFoundUser(null);
    setSearchError("");
    setSubmitError("");
    setIssued({});
    setForm({ date: nowStr(), remarks: "", reference_name: "" });
    onClose();
  };

  return (
    <>
    <RedFlagPopup user={open ? foundUser : null} />
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: { xs: "100vw", sm: 640 }, p: 3, overflowY: "auto", height: "100%" }}>

        <Typography variant="h5" fontWeight={700}>Issue Float</Typography>
        <Typography color="text.secondary" variant="body2" mb={2}>
          One-time chip advance. Nothing is deducted from the wallet — repaid first on cash-out or deposit.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography fontWeight={600} mb={1}>Search Player</Typography>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            fullWidth size="small" label="Phone / Player ID"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained" onClick={handleSearch}
            disabled={searching || !phone.trim()}
            sx={{ minWidth: 90, whiteSpace: "nowrap" }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {searchError && <Alert severity="error" sx={{ mb: 1 }}>{searchError}</Alert>}

        {foundUser?.red_flag && (
          <Alert severity="error" icon={<FlagIcon />} sx={{ mb: 2, fontWeight: 600 }}>
            RED FLAG — {foundUser.red_flag_remark || "This player has been flagged"}
          </Alert>
        )}

        {foundUser && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
            <Typography fontWeight={700} variant="subtitle1">{foundUser.name || "—"}</Typography>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player ID</Typography>
                <Typography fontWeight={600} variant="body2">{foundUser.playerId || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Phone</Typography>
                <Typography fontWeight={600} variant="body2">{maskPhone(foundUser.phone) || "—"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Float Outstanding</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: floatOpen > 0 ? "#ff6b6b" : "#38ef7d" }}>
                  {floatOpen.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Distributed</Typography>
                <Typography fontWeight={700} variant="h6">{Number(foundUser.loan_distributed || 0).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {floatOpen > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This player already has an open float of {floatOpen.toLocaleString()}. A re-issue adds to it — total due becomes {(floatOpen + totalAmount).toLocaleString()}.
          </Alert>
        )}

        <Typography fontWeight={700} mb={1}>Chips to Advance</Typography>
        {stockLoading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
        ) : (
          <FloatChipTable chips={stockChips} qty={issued} setQty={setIssued} qtyLabel="Qty to Issue" capByStock />
        )}

        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, background: "linear-gradient(135deg,#e65100,#f9a825)", color: "#fff" }}>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>Float Amount</Typography>
          <Typography variant="h5" fontWeight={700}>{totalAmount.toLocaleString()}</Typography>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth required size="small" label="Reference Name"
              placeholder="Who referred / approved this float"
              value={form.reference_name}
              onChange={(e) => setForm({ ...form, reference_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} size="small" label="Remarks (optional)"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Grid>

          {submitError && (
            <Grid item xs={12}><Alert severity="error">{submitError}</Alert></Grid>
          )}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained" color="warning" onClick={handleSave}
                disabled={loading || !foundUser || !form.date || !form.reference_name.trim() || totalAmount === 0 || overStock}
              >
                {loading ? "Saving..." : "Issue Float"}
              </Button>
            </Box>
          </Grid>
        </Grid>

      </Box>
    </Drawer>
    </>
  );
}
